import { getCachedSegmentPsd } from '../../../shared/spectral';
import type {
  ChartViewId,
  CustomTimeSegment,
  SpectralWorkspaceSession,
} from '../types';
import {
  filterSessionEventsForSegment,
  getWorkspaceAvailableSeconds,
  resolveSegmentBounds,
} from '../utils/resolveSpectraSegment';
import type { ExcelExportSheet } from './buildExcelWorkbook';
import {
  resolveTrendExportViewIds,
  resolveViewLabel,
  resolveViewSheetLabel,
} from './computeTrendExportSeries';

export type SpectraExportContext = {
  readySessions: SpectralWorkspaceSession[];
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  selectedSessionId: string | null;
  spectraStart: string | null;
  spectraEnd: string | null;
  logScale: boolean;
};

export const buildSpectraExportSheetName = (viewSheetLabel: string) =>
  `Spettri - ${viewSheetLabel}`;

const filterByFrequency = (
  frequencies: number[],
  psdLf: number[],
  psdHf: number[],
  spectraStart: string | null,
  spectraEnd: string | null
) => {
  const startFrequency = spectraStart ? Number(spectraStart) : -Infinity;
  const endFrequency = spectraEnd ? Number(spectraEnd) : Infinity;

  return frequencies
    .map((frequency, index) => ({
      frequency,
      lf: psdLf[index],
      hf: psdHf[index],
    }))
    .filter(
      point =>
        point.frequency >= startFrequency && point.frequency <= endFrequency
    );
};

export type SpectraExportRow = Record<string, string | number>;

export const buildSpectraExportDataRowsForView = (
  context: SpectraExportContext,
  viewId: ChartViewId
): SpectraExportRow[] => {
  const availableSeconds = getWorkspaceAvailableSeconds(context.readySessions);
  const segmentBounds = resolveSegmentBounds({
    activeViewId: viewId,
    customSegments: context.customSegments,
    availableSeconds,
    trendStart: context.trendStart,
    trendEnd: context.trendEnd,
  });

  const viewLabel = resolveViewLabel(viewId, context.customSegments);
  const viewSheetLabel = resolveViewSheetLabel(viewId, context.customSegments);

  if (!segmentBounds) {
    return [];
  }

  const sessionsToExport = context.selectedSessionId
    ? context.readySessions.filter(
        session => session.sessionId === Number(context.selectedSessionId)
      )
    : context.readySessions;

  return sessionsToExport.flatMap((session): SpectraExportRow[] => {
    const segmentEvents = filterSessionEventsForSegment(
      session.events ?? [],
      segmentBounds.startSecond,
      segmentBounds.endSecond
    );
    const result = getCachedSegmentPsd(
      session.sessionId,
      segmentBounds.startSecond,
      segmentBounds.endSecond,
      segmentEvents
    );

    if (!result.ok) {
      return [
        {
          Tratto: viewSheetLabel,
          Vista: viewLabel,
          session_id: session.sessionId,
          secondo_inizio: segmentBounds.startSecond,
          secondo_fine: segmentBounds.endSecond,
          frequenza_hz: '',
          psd_lf: '',
          psd_hf: '',
          psd_lf_grafico: '',
          psd_hf_grafico: '',
          scala_log: context.logScale ? 'si' : 'no',
          errore: result.reason,
        },
      ];
    }

    const points = filterByFrequency(
      result.psd.frequencies,
      result.psd.psd_lf,
      result.psd.psd_hf,
      context.spectraStart,
      context.spectraEnd
    );

    return points.map(point => ({
      Tratto: viewSheetLabel,
      Vista: viewLabel,
      session_id: session.sessionId,
      secondo_inizio: segmentBounds.startSecond,
      secondo_fine: segmentBounds.endSecond,
      frequenza_hz: point.frequency,
      psd_lf: point.lf,
      psd_hf: point.hf,
      psd_lf_grafico: context.logScale ? Math.log10(point.lf + 1e-6) : point.lf,
      psd_hf_grafico: context.logScale ? Math.log10(point.hf + 1e-6) : point.hf,
      scala_log: context.logScale ? 'si' : 'no',
      errore: '',
    }));
  });
};

export const buildSpectraExportSheets = (
  context: SpectraExportContext
): ExcelExportSheet[] =>
  resolveTrendExportViewIds(context.customSegments).map(viewId => ({
    name: buildSpectraExportSheetName(
      resolveViewSheetLabel(viewId, context.customSegments)
    ),
    rows: buildSpectraExportDataRowsForView(context, viewId),
  }));

/** @deprecated Use buildSpectraExportSheets */
export const buildSpectraExport = (
  context: SpectraExportContext,
  viewId: ChartViewId = 'full'
) => buildSpectraExportDataRowsForView(context, viewId);
